import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Lock, Mail, User } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

const Signup = () => {
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const { signup } = useAuth()

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors }
    } = useForm()

    const password = watch('password')

    const onSubmit = async (data) => {
        setIsLoading(true)
        setError('')
        try {
            await signup(data.username, data.email, data.password)
        } catch (error) {
            setError(error.message || 'Signup failed. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex">
            {/* Left side - Brand section */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-700 via-indigo-800 to-cyan-700 p-8">
                <div className="flex flex-col justify-center items-center text-center text-white w-full">
                    <img src="/assets/sharevision-logo.svg" alt="ShareVision Logo" className="h-20 mb-4" />
                    <p className="text-lg text-cyan-200 mb-4">SHAR Projects Pvt. Ltd</p>
                    <p className="text-xl text-cyan-100 font-medium">Camera Management System</p>
                </div>
            </div>

            {/* Right side - Signup form */}
            <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100 py-8 px-4">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="flex flex-col items-center mb-6 lg:hidden text-center">
                        <img src="/assets/sharevision-logo.svg" alt="ShareVision Logo" className="h-14 mb-2" />
                        <p className="text-sm text-gray-600">SHAR Projects Pvt. Ltd</p>
                        <p className="text-sm text-gray-500">Camera Management System</p>
                    </div>
                    <div className="flex flex-col items-center mb-8">
                        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
                            Create your account
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-600">
                            Already have an account?{' '}
                            <Link
                                to="/login"
                                className="font-medium text-cyan-600 hover:text-cyan-500 transition"
                            >
                                sign in here
                            </Link>
                        </p>
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-700">
                                <strong>Company Registration Only:</strong> Registration is restricted to authorized company email domains (@shar.in, @bgr.com, @sharprojects.in, or @bgrmining.com)
                            </p>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                        {/* Error message */}
                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                                        Username
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            id="username"
                                            type="text"
                                            autoComplete="username"
                                            className="input pl-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="Enter your username"
                                            {...register('username', {
                                                required: 'Username is required',
                                                minLength: {
                                                    value: 3,
                                                    message: 'Username must be at least 3 characters'
                                                },
                                                maxLength: {
                                                    value: 30,
                                                    message: 'Username must be less than 30 characters'
                                                },

                                            })}
                                        />
                                    </div>
                                    {errors.username && (
                                        <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                        Email address
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            id="email"
                                            type="email"
                                            autoComplete="email"
                                            className="input pl-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="Enter your email"
                                            {...register('email', {
                                                required: 'Email is required',
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: 'Invalid email address'
                                                }
                                            })}
                                        />
                                    </div>
                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                        Password
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Lock className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            className="input pl-10 pr-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="Enter your password"
                                            {...register('password', {
                                                required: 'Password is required',
                                                minLength: {
                                                    value: 6,
                                                    message: 'Password must be at least 6 characters'
                                                },
                                                pattern: {
                                                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                                                    message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
                                                }
                                            })}
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                        Confirm Password
                                    </label>
                                    <div className="mt-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Lock className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            className="input pl-10 pr-10 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition w-full"
                                            placeholder="Confirm your password"
                                            {...register('confirmPassword', {
                                                required: 'Please confirm your password',
                                                validate: value => value === password || 'Passwords do not match'
                                            })}
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            tabIndex={-1}
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <Eye className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="btn w-full py-3 rounded-lg text-lg font-semibold shadow-md bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white transition"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Creating account...
                                        </div>
                                    ) : (
                                        'Create account'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Signup 